import React, { useState, useEffect } from "react";
import axios from "../../../utils/axiosInstance";
import Button from "../../ui/Button";

/**
 * Reusable Task picker modal
 * Allows selecting tasks from the registry
 */
const TaskPickerModal = ({
  isOpen,
  onClose,
  onSelect,
  selectionMode = "multiple", // "single" or "multiple"
  existingIds = [] // IDs of already selected tasks to exclude
}) => {
  const [tasks, setTasks] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchTasks();
      setSelectedIds([]);
      setSearchTerm("");
    }
  }, [isOpen]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/registry/tasks", {
        params: { status: "active", limit: 200 }
      });
      setTasks(res.data.items || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      alert("Failed to load tasks from registry");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (taskId) => {
    if (selectionMode === "single") {
      setSelectedIds([taskId]);
    } else {
      setSelectedIds(prev =>
        prev.includes(taskId)
          ? prev.filter(id => id !== taskId)
          : [...prev, taskId]
      );
    }
  };

  const handleSelect = () => {
    const selected = tasks.filter(t => selectedIds.includes(t._id));
    onSelect(selected);
    onClose();
  };

  const filteredTasks = tasks.filter(t => {
    // Exclude already selected
    if (existingIds.includes(t._id)) return false;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        t.name?.toLowerCase().includes(term) ||
        t.description?.toLowerCase().includes(term) ||
        t.tags?.some(tag => tag.toLowerCase().includes(term))
      );
    }
    return true;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold mb-4">
            Add Task{selectionMode === "multiple" ? "s" : ""} from Registry
          </h2>

          {/* Search */}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, description, or tags..."
            className="w-full px-3 py-2 border rounded"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <p className="text-gray-500">Loading tasks...</p>
          ) : filteredTasks.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              {searchTerm
                ? "No tasks match your search"
                : existingIds.length > 0
                ? "All available tasks have been added"
                : "No tasks available in registry"}
            </p>
          ) : (
            <div className="space-y-2">
              {filteredTasks.map((task) => {
                const isSelected = selectedIds.includes(task._id);
                const subtaskCount = task.subtaskRefs?.length || 0;

                return (
                  <label
                    key={task._id}
                    className={`block p-4 border rounded cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start">
                      <input
                        type={selectionMode === "single" ? "radio" : "checkbox"}
                        checked={isSelected}
                        onChange={() => toggleSelection(task._id)}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{task.name}</div>
                        {task.description && (
                          <div className="text-sm text-gray-600 mt-1">
                            {task.description}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-2">
                          {subtaskCount} subtask{subtaskCount !== 1 ? 's' : ''}
                          {task.weight && ` • Weight: ${task.weight}`}
                          {task.estimatedHours && ` • ${task.estimatedHours}h est.`}
                          {task.tags && task.tags.length > 0 && (
                            <span className="ml-2">
                              {task.tags.map(tag => (
                                <span key={tag} className="inline-block bg-gray-100 rounded px-1 ml-1">
                                  {tag}
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {selectedIds.length > 0 && `${selectedIds.length} selected`}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="solid"
              onClick={handleSelect}
              disabled={selectedIds.length === 0}
            >
              Add {selectedIds.length > 0 && `(${selectedIds.length})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskPickerModal;
